import React, { useState, useEffect } from "react";
import Person from "./Person";

const TOKEN_KEY = "scDirectoryToken";
const LOGOUT_EVENT = "scDirectoryLogout";

/* ============================================================
   Uniform portrait photo wrapper
   Aspect ratio: ~5:6 (slightly taller than square)
   ============================================================ */
const PortraitPhoto = ({ src, alt, onError }) => (
  <div className="w-full overflow-hidden rounded-lg bg-neutral-200">
    <div className="relative w-full">
      <div className="pt-[120%]" />
      <img
        src={src}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        decoding="async"
        onError={onError}
      />
    </div>
  </div>
);

const People = () => {
  const [people, setPeople] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [open, setOpen] = useState(false);
  const toggleFilters = () => setOpen((o) => !o);

  const [filters, setFilters] = useState({
    first: "",
    last: "",
    state: "",
    sort: "",
  });

  const sortOptions = [
    "First Name A-Z",
    "First Name Z-A",
    "Last Name A-Z",
    "Last Name Z-A",
    "Start Year Ascending",
    "Start Year Descending",
  ];

  const stateOptions = [
    "AK", "AL", "AR", "AS", "AZ", "CA", "CO", "CT", "DC", "DE", "FL", "GA", "GU", "HI", "IA", "ID",
    "IL", "IN", "KS", "KY", "LA", "MA", "MD", "ME", "MI", "MN", "MO", "MP", "MS", "MT", "NC", "ND",
    "NE", "NH", "NJ", "NM", "NV", "NY", "OH", "OK", "OR", "PA", "PR", "RI", "SC", "SD", "TN", "TX",
    "UM", "UT", "VA", "VI", "VT", "WA", "WI", "WV", "WY", "Alberta, Canada", "England"
  ];

  function forceLogout(message) {
    localStorage.removeItem(TOKEN_KEY);

    window.dispatchEvent(
      new CustomEvent(LOGOUT_EVENT, {
        detail: { message },
      })
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const updateResults = () => {
    let updated = [...people];

    if (filters.state) {
      updated = updated.filter((p) => p.Mailing_State_Province === filters.state);
    }

    if (filters.first) {
      const f = filters.first.toLowerCase();
      updated = updated.filter((p) =>
        (p.First_Name || "").toLowerCase().includes(f)
      );
    }

    if (filters.last) {
      const l = filters.last.toLowerCase();
      updated = updated.filter((p) =>
        (p.Last_Name || "").toLowerCase().includes(l)
      );
    }

    switch (filters.sort) {
      case "First Name A-Z":
        updated.sort((a, b) => (a.First_Name || "").localeCompare(b.First_Name || ""));
        break;
      case "First Name Z-A":
        updated.sort((a, b) => (b.First_Name || "").localeCompare(a.First_Name || ""));
        break;
      case "Last Name A-Z":
        updated.sort((a, b) => (a.Last_Name || "").localeCompare(b.Last_Name || ""));
        break;
      case "Last Name Z-A":
        updated.sort((a, b) => (b.Last_Name || "").localeCompare(a.Last_Name || ""));
        break;
      case "Start Year Ascending":
        updated.sort((a, b) => (+a.Start_Year || 0) - (+b.Start_Year || 0));
        break;
      case "Start Year Descending":
        updated.sort((a, b) => (+b.Start_Year || 0) - (+a.Start_Year || 0));
        break;
      default:
        break;
    }

    setResults(updated);
  };

  useEffect(() => {
    const loadEmployees = async () => {
      setLoading(true);
      setError(false);

      const token = localStorage.getItem(TOKEN_KEY);

      if (!token) {
        forceLogout("Please log in to view the directory.");
        return;
      }

      try {
        const res = await fetch(`/api/employees?top=1000`, {
          method: "GET",
          headers: {
            "x-sc-directory-token": token,
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.status === 401) {
          forceLogout("Your session expired. Please log in again.");
          return;
        }

        if (!res.ok) {
          const body = await res.text();
          console.error("Function API error", res.status, body);
          throw new Error(`API error: ${res.status}`);
        }

        const json = await res.json();
        const rawData = Array.isArray(json) ? json : (json.rows ?? []);

        const activeData = rawData.filter(
          (p) => Number(p.Is_Active) === 1
        );

        activeData.sort((a, b) => {
          const ln = (a.Last_Name || "").localeCompare(b.Last_Name || "");
          return ln !== 0
            ? ln
            : (a.First_Name || "").localeCompare(b.First_Name || "");
        });

        setPeople(activeData);
        setResults(activeData);
      } catch (err) {
        console.error("Error fetching employees", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadEmployees();
  }, []);

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center px-6">
          <h1 className="text-3xl font-bold mb-2">
            The Sand Cherry directory is temporarily unavailable.
          </h1>
          <p className="text-lg text-gray-700">
            Please refresh the page in a few seconds. If the problem persists,
            contact the site administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row">
        <button
          onClick={toggleFilters}
          className="sm:hidden bg-sky-950 hover:bg-sky-700 text-white font-bold py-2 px-4 mx-4 mb-5 rounded"
        >
          Show Filters
        </button>

        {(open || typeof window === "undefined" || window.innerWidth >= 640) && (
          <div className="w-full sm:w-1/4 bg-neutral-300 rounded mx-3 p-4 mb-6 sm:mb-0">
            <h2 className="text-xl font-bold mb-4">Filters:</h2>

            <input
              name="first"
              value={filters.first}
              onChange={handleChange}
              placeholder="First Name"
              className="shadow border rounded w-full py-2 px-3 mb-4 focus:outline-none"
            />

            <input
              name="last"
              value={filters.last}
              onChange={handleChange}
              placeholder="Last Name"
              className="shadow border rounded w-full py-2 px-3 mb-4 focus:outline-none"
            />

            <select
              name="state"
              value={filters.state ?? ""}
              onChange={handleChange}
              className={`shadow border rounded w-full py-2 px-3 mb-4 focus:outline-none ${
                !filters.state ? "text-gray-400" : "text-gray-900"
              }`}
            >
              <option value="" disabled hidden>
                State (e.g., CO)
              </option>
              {stateOptions.map((opt, i) => (
                <option key={i} value={opt} className="text-gray-900">
                  {opt}
                </option>
              ))}
            </select>

            <select
              name="sort"
              value={filters.sort ?? ""}
              onChange={handleChange}
              className={`shadow border rounded w-full py-2 px-3 mb-4 focus:outline-none ${
                !filters.sort ? "text-gray-400" : "text-gray-900"
              }`}
            >
              <option value="" disabled hidden>
                Sort…
              </option>
              {sortOptions.map((opt, i) => (
                <option key={i} value={opt} className="text-gray-900">
                  {opt}
                </option>
              ))}
            </select>

            <button
              onClick={updateResults}
              className="bg-sky-950 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded"
            >
              Filter
            </button>
          </div>
        )}

        <div className="w-full sm:flex-1">
          {loading ? (
            <h2 className="text-center text-3xl">Loading Cherries 🍒</h2>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 p-3">
              {results.map((p) => (
                <Person key={p.Employee_ID} person={p} PortraitPhoto={PortraitPhoto} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default People;